// Rummy 500 engine (pure), two players: you and the computer. Rules after Pagat's 500 Rum: 13 cards each; the next card
// starts the discard pile, which is kept spread so every card in it shows. On your turn draw the top card of the stock,
// or take any card from the discard pile together with every card above it: the card you took (the deepest) must be
// melded or laid off at once, this turn. Then meld sets (3 or 4 of a rank) and runs (3 or more in a suit; the Ace is
// high or low, A-2-3 or Q-K-A, never K-A-2), lay off cards on any meld on the table, yours or the other player's, and
// discard one card. The hand ends when a player has no cards left, or when the stock is empty at a player's draw.
// Each player scores the cards they melded or laid off, less the cards still in their hand: an Ace 15 (but 5 in an
// A-2-3 run), 10s and pictures 10, the rest face value. First to 500 wins.
import { type Card, cardName, fullDeck, rankOf, suitOf } from "./card"
import { seeded, shuffle } from "../lib/rng"

export type Level = "easy" | "standard" | "hard"
export const LEVELS: Level[] = ["easy", "standard", "hard"]
export type Who = 0 | 1
export const TARGET = 500
export const HAND = 13
/** The rules: Rummy 500 (the default), or basic Rummy (the /rummy page): ten cards each, only the top of the discard
 *  pile may be taken and it needn't be melded, the pile is turned over when the stock runs out, the player who goes out
 *  scores what the other still holds (Ace 1), first to 100. */
export type Rules = "500" | "basic"
export const handSize = (r: Rules | undefined): number => (r === "basic" ? 10 : HAND)
export const target = (r: Rules | undefined): number => (r === "basic" ? 100 : TARGET)

export interface TableMeld { kind: "set" | "run"; cards: Card[]; by: Who[] }
export interface RummyState {
  seed: string
  rules?: Rules
  hands: [Card[], Card[]]
  stock: Card[]
  discard: Card[] // bottom first; the last is the top
  melds: TableMeld[]
  turn: Who
  dealer: Who
  phase: "draw" | "play" | "over"
  /** The card taken from the discard pile that must be melded this turn. */
  mustUse: Card | null
  /** Cards each player has taken from the discard pile (everyone saw them taken). */
  taken?: [Card[], Card[]]
  scores: [number, number]
  hand: number
  moves: number
  result: { melded: [number, number]; left: [number, number]; points: [number, number]; out: Who | null } | null
}

const other = (w: Who): Who => (1 - w) as Who
/** What a card counts: Ace 15, 10s and pictures 10, the rest face value. */
const value = (c: Card, rules?: Rules): number => (rankOf(c) === 1 ? (rules === "basic" ? 1 : 15) : rankOf(c) >= 10 ? 10 : rankOf(c))
export const cardPoints = value
export const handPoints = (h: readonly Card[], rules?: Rules): number => h.reduce((n, c) => n + value(c, rules), 0)
/** What a card on the table scores: an Ace in a low run (A-2-3) counts 5. */
export function meldCardPoints(m: TableMeld, c: Card): number {
  if (rankOf(c) === 1 && m.kind === "run" && m.cards.some((x) => rankOf(x) === 2)) return 5
  return value(c)
}

export function deal(seed: string, dealer: Who = 1, scores: [number, number] = [0, 0], hand = 1, rules: Rules = "500"): RummyState {
  const deck = shuffle(fullDeck(), seeded(`rummy:${seed}:${hand}`))
  const hands: [Card[], Card[]] = [[], []]
  for (let i = 0; i < handSize(rules); i++) for (const p of [other(dealer), dealer]) hands[p].push(deck.pop()!)
  const up = deck.pop()!
  return { seed, rules, hands, stock: deck, discard: [up], melds: [], turn: other(dealer), dealer, phase: "draw", mustUse: null, scores, hand, moves: 0, result: null }
}

// ---- melds -------------------------------------------------------------------------------------------------------

/** The cards in run order if they make a run (Ace low or high), else null. */
export function asRun(cards: readonly Card[]): Card[] | null {
  if (cards.length < 3 || new Set(cards.map(suitOf)).size !== 1) return null
  for (const aceHigh of [false, true]) {
    const pos = (c: Card): number => (rankOf(c) === 1 && aceHigh ? 14 : rankOf(c))
    const sorted = cards.slice().sort((a, b) => pos(a) - pos(b))
    if (sorted.every((c, i) => i === 0 || pos(c) === pos(sorted[i - 1]!) + 1)) return sorted
  }
  return null
}
export const isSet = (cards: readonly Card[]): boolean => cards.length >= 3 && cards.length <= 4 && new Set(cards.map(rankOf)).size === 1
export const meldKind = (cards: readonly Card[]): "set" | "run" | null => (isSet(cards) ? "set" : asRun(cards) ? "run" : null)

/** Can `c` be laid off on meld `m`? */
export function fitsMeld(m: TableMeld, c: Card): boolean {
  return m.kind === "set" ? isSet([...m.cards, c]) : asRun([...m.cards, c]) !== null
}

/** Every set and run that can be made from `cards` (sets of 3 and 4, runs of 3 or more). */
export function allMelds(cards: readonly Card[]): Card[][] {
  const out: Card[][] = []
  const byRank = new Map<number, Card[]>()
  for (const c of cards) byRank.set(rankOf(c), [...(byRank.get(rankOf(c)) ?? []), c])
  for (const cs of byRank.values()) {
    if (cs.length === 3) out.push(cs.slice())
    if (cs.length === 4) { out.push(cs.slice()); for (let skip = 0; skip < 4; skip++) out.push(cs.filter((_, i) => i !== skip)) }
  }
  for (let s = 0; s < 4; s++) {
    const have = new Map(cards.filter((c) => suitOf(c) === s).map((c) => [rankOf(c), c]))
    // Ranks 1-14, where 14 is the Ace again (high).
    const at = (r: number): Card | undefined => have.get(r === 14 ? 1 : r)
    for (let start = 1; start <= 12; start++) {
      const run: Card[] = []
      for (let r = start; r <= 14 && at(r) !== undefined; r++) {
        run.push(at(r)!)
        if (run.length >= 3) out.push(run.slice())
      }
    }
  }
  return out
}

/** The melds to put down from a hand that score the most, using each card once. */
export function bestMelds(cards: readonly Card[]): Card[][] {
  const melds = allMelds(cards)
  let best: Card[][] = [], bestPts = 0
  const go = (from: number, used: Set<Card>, chosen: Card[][], pts: number): void => {
    if (pts > bestPts) { bestPts = pts; best = chosen.map((m) => m.slice()) }
    for (let i = from; i < melds.length; i++) {
      const m = melds[i]!
      if (m.some((c) => used.has(c))) continue
      for (const c of m) used.add(c)
      chosen.push(m)
      go(i + 1, used, chosen, pts + m.reduce((n, c) => n + value(c), 0))
      chosen.pop()
      for (const c of m) used.delete(c)
    }
  }
  go(0, new Set(), [], 0)
  return best
}

// ---- moves -------------------------------------------------------------------------------------------------------

const withHand = (s: RummyState, who: Who, h: Card[]): [Card[], Card[]] => (who === 0 ? [h, s.hands[1]] : [s.hands[0], h])

export function drawStock(s: RummyState): RummyState {
  if (s.phase !== "draw") return s
  // Basic Rummy turns the discard pile over for a new stock (the top card stays); 500 ends the hand instead.
  if (!s.stock.length && s.rules === "basic" && s.discard.length > 1) return drawStock({ ...s, stock: s.discard.slice(0, -1).reverse(), discard: s.discard.slice(-1) })
  if (!s.stock.length) return endHand(s, null)
  const c = s.stock[s.stock.length - 1]!
  return { ...s, stock: s.stock.slice(0, -1), hands: withHand(s, s.turn, [...s.hands[s.turn], c]), phase: "play", moves: s.moves + 1 }
}

/** Why the card at `index` in the discard pile can't be taken now (it must be meldable at once), or null. */
export function whyNotTake(s: RummyState, index: number): string | null {
  if (s.phase !== "draw") return "You've drawn already."
  if (index < 0 || index >= s.discard.length) return "That card isn't in the discard pile."
  if (s.rules === "basic") return index === s.discard.length - 1 ? null : "In Rummy you can take only the top card of the discard pile."
  const taken = s.discard.slice(index)
  const c = taken[0]!
  const pool = [...s.hands[s.turn], ...taken]
  if (allMelds(pool).some((m) => m.includes(c)) || s.melds.some((m) => fitsMeld(m, c))) return null
  return `You can take the ${index === s.discard.length - 1 ? "top card" : "pile down to that card"} only if you can meld it at once, and nothing in your hand goes with it.`
}

export function takeDiscard(s: RummyState, index: number): RummyState {
  if (whyNotTake(s, index)) return s
  const taken = s.discard.slice(index)
  const seen: [Card[], Card[]] = [...(s.taken ?? [[], []])] as [Card[], Card[]]
  seen[s.turn] = [...seen[s.turn], ...taken]
  return { ...s, discard: s.discard.slice(0, index), hands: withHand(s, s.turn, [...s.hands[s.turn], ...taken]), phase: "play", mustUse: s.rules === "basic" ? null : taken[0]!, taken: seen, moves: s.moves + 1 }
}

function afterTablePlay(s: RummyState, hand: Card[], melds: TableMeld[], used: Card[]): RummyState {
  const mustUse = s.mustUse !== null && used.includes(s.mustUse) ? null : s.mustUse
  const t = { ...s, hands: withHand(s, s.turn, hand), melds, mustUse, moves: s.moves + 1 }
  return hand.length ? t : endHand(t, s.turn)
}

/** After a play, can the card taken from the discard pile still go down this turn? If not, the player would be stuck:
 *  every discard is refused until it's used, and there's no undo. */
function strandsTaken(s: RummyState, hand: readonly Card[], melds: readonly TableMeld[], used: readonly Card[]): string | null {
  const c = s.mustUse
  if (c === null || used.includes(c)) return null
  if (allMelds(hand).some((m) => m.includes(c)) || melds.some((m) => fitsMeld(m, c))) return null
  return `That would leave the ${cardName(c)} you took from the discard pile with nothing to go with. Put it down first.`
}

export function whyNotMeld(s: RummyState, cards: readonly Card[]): string | null {
  if (s.phase !== "play") return s.phase === "draw" ? "Draw a card first: from the stock, or from the discard pile." : "This hand is over."
  if (!cards.every((c) => s.hands[s.turn].includes(c))) return "Those cards aren't all in your hand."
  if (cards.length < 3) return "A meld needs at least 3 cards: three or four of a kind, or three or more in a row in one suit."
  const kind = meldKind(cards)
  if (!kind) return "Those cards don't make a meld: three or four of a kind, or three or more in a row in one suit (Ace high or low)."
  return strandsTaken(s, s.hands[s.turn].filter((c) => !cards.includes(c)), [...s.melds, { kind, cards: [...cards], by: [] }], cards)
}

export function meld(s: RummyState, cards: readonly Card[]): RummyState {
  if (whyNotMeld(s, cards)) return s
  const kind = meldKind(cards)!
  const ordered = kind === "run" ? asRun(cards)! : cards.slice().sort((a, b) => suitOf(a) - suitOf(b))
  const hand = s.hands[s.turn].filter((c) => !cards.includes(c))
  return afterTablePlay(s, hand, [...s.melds, { kind, cards: ordered, by: ordered.map(() => s.turn) }], [...cards])
}

export function whyNotLayOff(s: RummyState, c: Card, at: number): string | null {
  if (s.phase !== "play") return s.phase === "draw" ? "Draw a card first." : "This hand is over."
  if (!s.hands[s.turn].includes(c)) return "That card isn't in your hand."
  const m = s.melds[at]
  if (!m) return "There's no meld there."
  if (!fitsMeld(m, c)) return "That card doesn't go on that meld."
  return strandsTaken(s, s.hands[s.turn].filter((x) => x !== c), s.melds.map((x, i) => (i === at ? { ...m, cards: [...m.cards, c] } : x)), [c])
}

export function layOff(s: RummyState, c: Card, at: number): RummyState {
  if (whyNotLayOff(s, c, at)) return s
  const m = s.melds[at]!
  const cards = m.kind === "run" ? asRun([...m.cards, c])! : [...m.cards, c]
  const by = cards.map((x) => (x === c ? s.turn : m.by[m.cards.indexOf(x)]!))
  const melds = s.melds.map((x, i) => (i === at ? { ...m, cards, by } : x))
  return afterTablePlay(s, s.hands[s.turn].filter((x) => x !== c), melds, [c])
}

export function whyNotDiscard(s: RummyState, c: Card): string | null {
  if (s.phase !== "play") return s.phase === "draw" ? "Draw a card first." : "This hand is over."
  if (!s.hands[s.turn].includes(c)) return "That card isn't in your hand."
  if (s.mustUse !== null) return `First meld the card you took from the discard pile, the ${cardName(s.mustUse)}.`
  return null
}

export function discardCard(s: RummyState, c: Card): RummyState {
  if (whyNotDiscard(s, c)) return s
  const hand = s.hands[s.turn].filter((x) => x !== c)
  const t: RummyState = { ...s, hands: withHand(s, s.turn, hand), discard: [...s.discard, c], turn: other(s.turn), phase: "draw", moves: s.moves + 1 }
  return hand.length ? t : endHand(t, s.turn)
}

function endHand(s: RummyState, out: Who | null): RummyState {
  if (s.rules === "basic") {
    // The player who went out scores every card the other still holds; nobody scores when the cards run out.
    const left: [number, number] = [handPoints(s.hands[0], "basic"), handPoints(s.hands[1], "basic")]
    const points: [number, number] = out === null ? [0, 0] : out === 0 ? [left[1], 0] : [0, left[0]]
    return { ...s, phase: "over", mustUse: null, result: { melded: [0, 0], left, points, out }, scores: [s.scores[0] + points[0], s.scores[1] + points[1]] }
  }
  const melded: [number, number] = [0, 0]
  for (const m of s.melds) m.cards.forEach((c, i) => { melded[m.by[i]!] += meldCardPoints(m, c) })
  const left: [number, number] = [handPoints(s.hands[0]), handPoints(s.hands[1])]
  const points: [number, number] = [melded[0] - left[0], melded[1] - left[1]]
  return { ...s, phase: "over", mustUse: null, result: { melded, left, points, out }, scores: [s.scores[0] + points[0], s.scores[1] + points[1]] }
}

export const gameOver = (s: RummyState): boolean => s.phase === "over" && s.scores.some((n) => n >= target(s.rules)) && s.scores[0] !== s.scores[1]
export const winner = (s: RummyState): Who => (s.scores[0] >= s.scores[1] ? 0 : 1)
export const nextHand = (s: RummyState, seed: string): RummyState => deal(seed, other(s.dealer), s.scores, s.hand + 1, s.rules ?? "500")

// ---- the computer (and Hint) -------------------------------------------------------------------------------------

/** The cards a hand would keep as near-melds: pairs of a rank, and two of a suit one or two apart. */
function nearMeld(hand: readonly Card[], c: Card): boolean {
  return hand.some((x) => x !== c && (rankOf(x) === rankOf(c) || (suitOf(x) === suitOf(c) && Math.abs(rankOf(x) - rankOf(c)) <= 2)))
}

/** Which discard to take (deepest index), or null for the stock: the depth whose taken card makes the most points of
 *  meld at once, less the cards picked up with it that don't help. */
export function chooseDraw(s: RummyState, level: Level, rand: () => number = Math.random): number | null {
  const hand = s.hands[s.turn]
  let best: number | null = null, bestScore = level === "easy" ? 8 : level === "hard" ? -5 : 4
  // Easy only looks at the top three cards; basic Rummy allows the top card only.
  const deepest = s.rules === "basic" ? s.discard.length - 1 : level === "easy" ? Math.max(0, s.discard.length - 3) : 0
  for (let i = s.discard.length - 1; i >= deepest; i--) {
    if (whyNotTake(s, i)) continue
    const taken = s.discard.slice(i)
    const pool = [...hand, ...taken]
    const now = bestMelds(pool).reduce((n, m) => n + m.reduce((k, c) => k + value(c), 0), 0) - bestMelds(hand).reduce((n, m) => n + m.reduce((k, c) => k + value(c), 0), 0)
    const layoffs = taken.filter((c) => s.melds.some((m) => fitsMeld(m, c))).reduce((n, c) => n + value(c), 0)
    const junk = taken.filter((c) => !nearMeld(pool, c) && !s.melds.some((m) => fitsMeld(m, c))).reduce((n, c) => n + value(c), 0)
    // Late in the game, extra cards are a bigger risk of being caught with them.
    // Hard picks up more freely (tuned in simulation: 56% of games against Standard, from 53%).
    const risk = level === "hard" ? (s.stock.length < 12 ? 0.3 : 0.1) : s.stock.length < 12 ? 0.8 : 0.4
    const score = now + layoffs - junk * risk + (level === "easy" ? rand() * 6 - 3 : 0)
    if (score > bestScore) { bestScore = score; best = i }
  }
  return best
}

export type Play = { kind: "meld"; cards: Card[] } | { kind: "layoff"; card: Card; at: number }

/** What to put on the table this turn: the card that must be used first, then the best melds, then lay-offs. Easy
 *  keeps some melds back; Standard and Hard put everything down (cards in hand count against you). */
export function choosePlays(s: RummyState, level: Level, rand: () => number = Math.random): Play[] {
  const out: Play[] = []
  let t = s
  for (let guard = 0; guard < 30 && t.phase === "play" && t.turn === s.turn; guard++) {
    const hand = t.hands[t.turn]
    let melds = bestMelds(hand)
    if (t.mustUse !== null) {
      // The card taken from the discard pile goes down first, at every level.
      const need = melds.find((m) => m.includes(t.mustUse!)) ?? allMelds(hand).find((m) => m.includes(t.mustUse!))
      if (need) { out.push({ kind: "meld", cards: need }); t = meld(t, need); continue }
      const at = t.melds.findIndex((m) => fitsMeld(m, t.mustUse!))
      if (at >= 0) { out.push({ kind: "layoff", card: t.mustUse, at }); t = layOff(t, t.mustUse, at); continue }
    }
    if (level === "easy") melds = melds.filter(() => rand() < 0.85)
    const m = melds[0]
    if (m) { out.push({ kind: "meld", cards: m }); t = meld(t, m); continue }
    let laid = false
    for (const c of hand) {
      const at = t.melds.findIndex((x) => fitsMeld(x, c))
      if (at >= 0 && (level !== "easy" || rand() < 0.85)) { out.push({ kind: "layoff", card: c, at }); t = layOff(t, c, at); laid = true; break }
    }
    if (!laid) break
  }
  return out
}

/** The card to throw: not part of a near-meld, not one the other player could lay off (Standard and Hard), and the
 *  highest such. Hard also won't throw a card that makes a meld with the two cards it would cover, or one that goes
 *  with the cards the other player took from the pile and still holds. */
export function chooseDiscard(s: RummyState, level: Level, rand: () => number = Math.random): Card | null {
  const hand = s.hands[s.turn]
  if (!hand.length) return null
  if (level === "easy" && rand() < 0.2) return hand[Math.floor(rand() * hand.length)]!
  // Cards the other player took from the pile and still holds: Hard won't throw what goes with them.
  const theirs = level === "hard" ? (s.taken?.[other(s.turn)] ?? []).filter((x) => s.hands[other(s.turn)].includes(x)) : []
  const gives = (c: Card): boolean => s.melds.some((m) => fitsMeld(m, c))
  const score = (c: Card): number => {
    let v = value(c)
    if (nearMeld(hand, c)) v -= 25
    if (level !== "easy" && gives(c)) v -= 40
    // Hard looks at the top of the discard pile it's about to cover: a card that melds with it hands over both.
    if (level === "hard" && allMelds([c, ...s.discard.slice(-2)]).length) v -= 30
    if (theirs.some((x) => rankOf(x) === rankOf(c) || (suitOf(x) === suitOf(c) && Math.abs(rankOf(x) - rankOf(c)) <= 2))) v -= 35
    return v
  }
  return hand.slice().sort((a, b) => score(b) - score(a))[0]!
}

export function playerHint(s: RummyState): { draw?: number | "stock"; plays?: Play[]; discard?: Card } | null {
  if (s.turn !== 0 || s.phase === "over") return null
  if (s.phase === "draw") { const d = chooseDraw(s, "standard"); return { draw: d ?? "stock" } }
  const plays = choosePlays(s, "standard")
  if (plays.length) return { plays }
  const d = chooseDiscard(s, "hard")
  return d === null ? null : { discard: d }
}
